import React, { useState, useEffect, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';
import { useGesture } from '@use-gesture/react';

interface LightboxProps {
  images: { url: string; description: string }[];
  initialIndex?: number;
  onClose: () => void;
  open: boolean;
}

const Lightbox: React.FC<LightboxProps> = ({
  images,
  initialIndex = 0,
  onClose,
  open,
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [loading, setLoading] = useState(true);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [open]);

  const preloadImage = useCallback((index: number) => {
    if (images[index]) {
      const img = new Image();
      img.src = images[index].url;
    }
  }, [images]);

  useEffect(() => {
    if (open) {
      setLoading(true);
      const img = new Image();
      img.src = images[currentIndex].url;
      img.onload = () => setLoading(false);

      // Preload adjacent images
      preloadImage(currentIndex - 1);
      preloadImage(currentIndex + 1);
    }
  }, [currentIndex, images, open, preloadImage]);

  const handlePrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  }, [images.length]);

  const handleNext = useCallback(() => {
    setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  }, [images.length]);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape') onClose();
    if (event.key === 'ArrowLeft') handlePrevious();
    if (event.key === 'ArrowRight') handleNext();
  }, [handleNext, handlePrevious, onClose]);

  useEffect(() => {
    if (open) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [open, handleKeyDown]);

  const bind = useGesture({
    onDrag: ({ movement: [mx, my], first, last }) => {
      if (first) setPosition({ x: 0, y: 0 });
      if (!last) {
        setPosition({ x: mx, y: my });
      } else {
        if (Math.abs(mx) > 100) {
          mx > 0 ? handlePrevious() : handleNext();
        }
        setPosition({ x: 0, y: 0 });
      }
    },
    onPinch: ({ offset: [d] }) => {
      const newScale = 1 + d / 200;
      setScale(Math.min(Math.max(newScale, 0.5), 3));
    },
  });

  return (
    <Dialog.Root open={open} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50"
          {...bind()}
        >
          <AnimatePresence mode="wait">
            <Dialog.Content
              className="fixed inset-0 flex items-center justify-center outline-none"
            >
              <Dialog.Title className="sr-only">
                Image {currentIndex + 1} of {images.length}
              </Dialog.Title>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="relative w-full h-full flex items-center justify-center"
              >
                {loading && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                )}

                <motion.img
                  key={currentIndex}
                  src={images[currentIndex].url}
                  alt={images[currentIndex].description}
                  className="max-h-[90vh] max-w-[90vw] object-contain select-none"
                  style={{
                    transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
                  }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  onDoubleClick={() => setScale(scale === 1 ? 2 : 1)}
                />

                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white px-4 py-2 rounded-full text-sm">
                  {currentIndex + 1} of {images.length}
                </div>

                <button
                  onClick={onClose}
                  className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
                  aria-label="Close lightbox"
                >
                  <X className="w-8 h-8" />
                </button>

                {images.length > 1 && (
                  <>
                    <button
                      onClick={handlePrevious}
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white transition-colors"
                      aria-label="Previous image"
                    >
                      <ChevronLeft className="w-8 h-8" />
                    </button>
                    <button
                      onClick={handleNext}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white transition-colors"
                      aria-label="Next image"
                    >
                      <ChevronRight className="w-8 h-8" />
                    </button>
                  </>
                )}
              </motion.div>
            </Dialog.Content>
          </AnimatePresence>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default Lightbox;